/**
 * Mark-complete control + crowd of avatars showing who else has done it.
 * Used inline in each session card on the Plan timeline.
 */

import { useEffect, useState } from 'react';
import { api } from '../api';
import { IconCheck } from './Icon';
import { Avatar } from './Avatar';
import { UiNode } from '../lib/UiNode';
import type { BoardDetail, User } from '../../shared/types';

interface Completion {
  userId: string;
  completedAt: number;
  name: string;
  avatarHue: number;
}

export function SessionCompletion({
  board,
  sessionId,
  me,
}: {
  board: BoardDetail;
  sessionId: string;
  me: User;
}) {
  const [rows, setRows] = useState<Completion[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .listSessionCompletions(board.id, sessionId)
      .then((r) => {
        if (!cancelled) {
          setRows(r.completions);
          setLoaded(true);
        }
      })
      .catch(() => setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [board.id, sessionId]);

  const doneByMe = rows.some((r) => r.userId === me.id);
  const others = rows.filter((r) => r.userId !== me.id);

  async function toggle() {
    if (busy) return;
    setBusy(true);
    // optimistic
    if (doneByMe) {
      setRows((cur) => cur.filter((r) => r.userId !== me.id));
    } else {
      setRows((cur) => [
        ...cur,
        {
          userId: me.id,
          completedAt: Math.floor(Date.now() / 1000),
          name: me.name,
          avatarHue: me.avatarHue,
        },
      ]);
    }
    try {
      await api.toggleSessionComplete(board.id, sessionId);
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return null;

  return (
    <div className="inline-flex items-center gap-2">
      <UiNode
        as="button"
        name="session-mark-done-button"
        entity="session"
        entityId={sessionId}
        action="mark_done"
        state={doneByMe ? 'done' : 'undone'}
        type="button"
        onClick={toggle}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs"
        style={{
          background: doneByMe ? 'var(--color-leaf)' : 'transparent',
          color: doneByMe ? 'var(--color-ink)' : 'var(--color-cream-3)',
          borderColor: doneByMe ? 'var(--color-leaf)' : 'var(--color-ink-rim)',
        }}
        title={doneByMe ? 'Mark not done' : 'Mark this complete for me'}
      >
        <IconCheck size={12} />
        {doneByMe ? 'Done' : 'Mark done'}
      </UiNode>
      {others.length > 0 && (
        <div className="flex items-center gap-1 text-xs text-cream-3">
          <span>+{others.length}</span>
          <div className="flex -space-x-1">
            {others.slice(0, 5).map((r) => (
              <span key={r.userId} title={`${r.name} marked done`}>
                <Avatar
                  user={{ id: r.userId, name: r.name, avatarHue: r.avatarHue, avatarUrl: null }}
                  size={16}
                />
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
